<template>
  <div ref="widget" class="city-fixed-static">
    <el-form size="mini">
      <el-card class="box-card dense">
        <div slot="header">City/State Rates</div>
        <Regions
          :hub="hub"
          :terminals="options.terminals"
          :depots="options.depots"
          :yards="options.yards"
          :places="options.places"
        />
        <fieldset class="form relative">
          <legend
            >Customers
            <div class="absolute right-0 -top-1">
              <el-button
                size="mini"
                class="narrow"
                @click="exportCsv('customer_rates')"
              >
                Export Customer Rates
              </el-button>
            </div>
          </legend>
          <table class="tbl">
            <thead>
              <tr>
                <th class="w-6">
                  <a @click="addCustomerCity" class="fas fa-plus-circle" />
                </th>
                <th class="w-56">
                  <el-input
                    v-model="customers.keyword"
                    size="mini"
                    clearable
                    placeholder="min 2 letters"
                  >
                    <template slot="append">
                      <i class="el-icon-search" />
                    </template>
                  </el-input>
                </th>
                <th v-for="region in regions" :key="region.id" class="w-60">
                  {{ region.name }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody :key="`customers${customers.identifier}`">
              <slot v-for="(row, index) in customers.items">
                <tr :key="index">
                  <td class="text-center">
                    <el-popconfirm
                      title="Are you sure to delete this city/state?"
                      confirmButtonText="Delete"
                      cancelButtonText="Cancel"
                      @confirm="removeCustomerCity(index)"
                    >
                      <a
                        class="fas fa-trash-alt"
                        slot="reference"
                        v-on:click.stop
                      />
                    </el-popconfirm>
                  </td>
                  <td @mouseenter="focusCustomerCity(row)">
                    <div class="flex items-center gap-1">
                      <div class="w-44">
                        <el-input
                          v-if="row.manual"
                          v-model="row.city"
                          size="mini"
                          :key="`city${index}`"
                          placeholer="City/State"
                          @blur="checkCustomerCity"
                        />
                        <map-suggest
                          v-else
                          v-model="row.city"
                          :cities="true"
                          :key="`map${index}`"
                          :appendBody="true"
                          @select="selectCustomerCity"
                        />
                      </div>
                      <i
                        class="fas fa-map-location-dot text-xs cursor-pointer text-blue-500"
                        :class="{ 'text-gray-300': row.manual }"
                        @click="manualCustomerCity(row)"
                      />
                      <el-tooltip
                        v-show="row.dup"
                        class="item"
                        effect="dark"
                        content="Duplicate City/State"
                      >
                        <i class="red fas fa-exclamation-circle" />
                      </el-tooltip>
                    </div>
                  </td>
                  <td v-for="region in regions" :key="region.id">
                    <div class="flex items-center justify-center gap-0.5">
                      <div class="w-16">
                        <el-input
                          v-model="row.regions[region.name].rate"
                          size="mini"
                          type="number"
                          :disabled="row.dup || isEmptyData(row.city)"
                        >
                          <template slot="prepend">C</template>
                        </el-input>
                      </div>
                      <div class="w-16">
                        <el-input
                          v-model="row.regions[region.name].toll"
                          size="mini"
                          type="number"
                          :disabled="row.dup || isEmptyData(row.city)"
                        >
                          <template slot="prepend">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 97 97"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M51.7902 52.9516V20.9516H51.3902L31.7902 8.85156L12.1902 21.0516H11.7902V81.1516H7.99023V89.0516H11.7902H51.7902H55.8902V81.1516H51.7902V62.8516L56.5902 59.8516L56.1902 50.2516L51.7902 52.9516ZM43.9902 57.3516H19.7902V28.9516H43.9902V57.3516Z"
                                fill="black"
                              />
                              <path
                                d="M88.1895 39.952L83.5895 32.752L80.6895 34.652L81.0895 44.452L88.1895 39.952Z"
                                fill="black"
                              />
                              <path
                                d="M76.9902 36.9516L70.4902 41.1516L69.9902 41.4516L70.3902 51.1516L70.7902 50.9516L77.2902 46.7516L77.6902 46.5516L77.2902 36.8516L76.9902 36.9516Z"
                                fill="black"
                              />
                              <path
                                d="M60.2898 47.5518L59.5898 48.0518L59.9898 57.6518L60.5898 57.3518L66.9898 53.2518L66.5898 43.5518L60.2898 47.5518Z"
                                fill="black"
                              />
                            </svg>
                          </template>
                        </el-input>
                      </div>
                      <el-popover
                        placement="right"
                        width="400"
                        trigger="click"
                        :disabled="row.dup"
                        @after-enter="
                          specificCustomers(
                            row.regions[region.name],
                            'customers',
                          )
                        "
                      >
                        <div class="customers"></div>
                        <a
                          slot="reference"
                          :class="{
                            red: !isEmptyData(
                              row.regions[region.name].customers,
                            ),
                          }"
                        >
                          <svg
                            class="w-4"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                            ></path>
                          </svg>
                        </a>
                      </el-popover>
                    </div>
                  </td>
                  <td></td>
                </tr>
              </slot>
            </tbody>
          </table>
          <div class="my-1" v-if="customers.total > 0">
            <el-pagination
              background
              layout="prev, pager, next"
              :page-size="customers.pageSize"
              :current-page="customers.page"
              :total="customers.total"
              @current-change="customersPageChange($event)"
            />
          </div>
        </fieldset>
        <fieldset class="form">
          <legend>Drivers</legend>
          <table class="tbl">
            <thead>
              <tr>
                <th class="w-6" rowspan="2">
                  <a @click="addDriverCity" class="fas fa-plus-circle" />
                </th>
                <th class="w-56" rowspan="2">
                  <el-input
                    v-model="drivers.keyword"
                    size="mini"
                    clearable
                    placeholder="min 2 letters"
                  >
                    <template slot="append">
                      <i class="el-icon-search" />
                    </template>
                  </el-input>
                </th>
                <th class="w-56" rowspan="2">Terminals</th>
                <th
                  v-for="(name, type) in options.driverTypes"
                  :key="type"
                  :colspan="actions.length"
                >
                  {{ name }}
                </th>
              </tr>
              <tr>
                <template v-for="(name, type) in options.driverTypes">
                  <th
                    v-for="action in actions"
                    :key="`${type}-${action}`"
                    class="w-16"
                  >
                    {{ action.capitalize() }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody :key="`drivers${drivers.identifier}`">
              <tr v-for="(row, index) in drivers.items" :key="index">
                <td class="text-center">
                  <el-popconfirm
                    title="Are you sure to delete this city/state?"
                    confirmButtonText="Delete"
                    cancelButtonText="Cancel"
                    @confirm="removeDriverCity(index)"
                  >
                    <a
                      class="fas fa-trash-alt"
                      slot="reference"
                      v-on:click.stop
                    />
                  </el-popconfirm>
                </td>
                <td @mouseenter="focusDriverCity(row)">
                  <div class="flex items-center gap-1">
                    <div class="w-44">
                      <el-input
                        v-if="row.manual"
                        v-model="row.city"
                        size="mini"
                        clearable
                        :key="`city${index}`"
                        placeholer="City/State"
                        @blur="checkDriverCity"
                      />
                      <map-suggest
                        v-else
                        v-model="row.city"
                        :cities="true"
                        :key="`map${index}`"
                        :appendBody="true"
                        @select="selectDriverCity"
                      />
                    </div>
                    <i
                      class="fas fa-map-location-dot text-xs cursor-pointer text-blue-500"
                      :class="{ 'text-gray-300': row.manual }"
                      @click="manualDriverCity(row)"
                    />
                    <el-tooltip
                      v-show="row.dup"
                      class="item"
                      effect="dark"
                      content="Duplicate City/State"
                    >
                      <i class="red fas fa-exclamation-circle" />
                    </el-tooltip>
                  </div>
                </td>
                <td>
                  <div class="flex items-center gap-1">
                    <el-popover
                      placement="bottom"
                      width="400"
                      trigger="click"
                      @show="setTerminalSelectorVisible(row, true)"
                      @hide="setTerminalSelectorVisible(row, false)"
                    >
                      <div v-if="row.terminalSelector">
                        <el-select
                          v-model="row.terminals"
                          size="mini"
                          multiple
                          clearable
                          filterable
                          style="width: 100%"
                        >
                          <el-option
                            v-for="(item, tIndex) in options.terminals"
                            :key="tIndex"
                            :label="item.name"
                            :value="item.id"
                          />
                        </el-select>
                      </div>
                      <a slot="reference">
                        <img src="@/images/terminal.svg?url" class="w-4" />
                      </a>
                    </el-popover>
                    <div class="terminal-names">
                      <div
                        v-for="(name, tIndex) in terminalNames(row)"
                        :key="tIndex"
                        class="item"
                      >
                        {{ name }}
                      </div>
                    </div>
                  </div>
                </td>
                <template v-for="(name, type) in options.driverTypes">
                  <td
                    v-for="action in actions"
                    :key="`${type}-${action}`"
                    class="w-16"
                  >
                    <el-input
                      v-model="row[type][action]"
                      size="mini"
                      type="number"
                      class="text-center"
                      :disabled="row.dup || isEmptyData(row.city)"
                    />
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
          <div class="my-1" v-if="drivers.total > 0">
            <el-pagination
              background
              layout="prev, pager, next"
              :page-size="drivers.pageSize"
              :current-page="drivers.page"
              :total="drivers.total"
              @current-change="driversPageChange($event)"
            />
          </div>
          <div class="bottom global-fees">
            <div class="text-sm">Global Fees</div>
            <div class="flex items-center gap-2">
              <div class="flex items-center flex-wrap gap-1">
                <label>Hazmat</label>
                <div
                  v-for="(name, type) in options.driverTypes"
                  :key="`global-${type}`"
                  :style="{ width: 6 * name.length + 60 + 'px' }"
                >
                  <el-input
                    v-model.number="baseRate.driver_globals.hazmat[type]"
                    size="mini"
                    type="number"
                  >
                    <template slot="prepend">{{ name }}</template>
                  </el-input>
                </div>
              </div>
              <el-divider direction="vertical" />
              <div class="flex items-center flex-wrap gap-1">
                <label>Tank</label>
                <div
                  v-for="(name, type) in options.driverTypes"
                  :key="`global-${type}`"
                  :style="{ width: 6 * name.length + 60 + 'px' }"
                >
                  <el-input
                    v-model.number="baseRate.driver_globals.tank[type]"
                    size="mini"
                    type="number"
                  >
                    <template slot="prepend">{{ name }}</template>
                  </el-input>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </el-card>
    </el-form>
    <el-card class="box-card dense">
      <div slot="header" class="flex items-center justify-between">
        <div>DOE Price FSC</div>
        <el-popover placement="bottom" width="400" trigger="click">
          <div class="p-2">
            <el-form size="mini" label-position="top" inline>
              <el-form-item label="Customer">
                <el-select v-model="fsc.customerId" filterable class="w-72">
                  <el-option
                    v-for="customer in options.customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                    :disabled="fscCustomerDisabled(customer)"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label=" ">
                <el-button
                  type="primary"
                  size="mini"
                  @click="addCustomerFsc"
                  :disabled="!fsc.customerId"
                >
                  Add
                </el-button>
              </el-form-item>
            </el-form>
          </div>
          <el-button slot="reference" size="mini" class="micro">
            Add Customer
          </el-button>
        </el-popover>
      </div>
      <div class="flex flex-nowrap overflow-x-auto gap-1 relative">
        <div
          v-for="(fsc, index) in baseRate.fsc"
          :key="fsc.id"
          class="relative"
        >
          <DoeFsc v-if="fsc.mode === 'doe'" :fsc="fsc" :options="options" />
          <el-popconfirm
            v-if="fsc.id"
            title="Are you sure to delete this FSC?"
            confirmButtonText="Delete"
            cancelButtonText="Cancel"
            @confirm="removeFsc(index)"
          >
            <a
              class="fas fa-trash-alt absolute top-2.5 left-0.5 gray"
              slot="reference"
              v-on:click.stop
            ></a>
          </el-popconfirm>
        </div>
      </div>
    </el-card>
    <Accessorial
      v-if="accessoriesReady"
      :accessories="baseRate.accessories"
      :regions="regions"
      :options="options"
      :driverMileageRate="driverMileageRate"
      @add="addAccessory"
      @remove="removeAccessory"
    />
    <div ref="customerOverrides">
      <div class="e-form" v-if="customer.visible">
        <div class="flex items-center gap-1 my-1">
          <el-select
            size="mini"
            v-model="customer.id"
            filterable
            :placeholder="searchCustomerPlaceholder"
            class="w-full"
          >
            <el-option-group
              v-for="group in customerOptions(customer.items)"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-option-group>
          </el-select>
          <el-button
            plain
            size="mini"
            type="primary"
            @click="addCustomerOverride(customer.items)"
            :disabled="!customer.id"
            >Add</el-button
          >
        </div>
        <ul class="stripe max-h-80 overflow-auto my-1">
          <li
            v-for="c in customer.items"
            :key="c.id"
            class="border px-1 py-0.5 stripe-item flex flex-col"
            :class="{ 'border-red-500': customer?.current?.id == c.id }"
          >
            <div class="flex items-center gap-1">
              <el-popconfirm
                title="Are you sure to delete this customized item?"
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                @confirm="removeCustomerOverride(customer.items, c.id)"
              >
                <a class="fas fa-trash-alt" slot="reference" v-on:click.stop />
              </el-popconfirm>
              <label class="grow text-xs">
                {{ customerName(c.id) }}
              </label>
              <img
                :src="$getSvgUrl('warehouse')"
                class="w-5"
                :class="{
                  'opacity-100': customer?.current?.id == c.id,
                  'opacity-40': customer?.current?.id != c.id,
                }"
                @click="specificCustomerWarehouses(c)"
              />
              <div class="w-20">
                <el-input v-model="c.rate" size="mini" type="number" />
              </div>
            </div>
            <ul
              class="max-h-20 bg-pink-50 border-t -mx-1 px-1 flex flex-col divide-y gap-0.5 empty:hidden"
            >
              <li v-for="w in c.warehouses" :key="w.id" class="py-0.5">
                <div class="flex items-center">
                  <svg
                    class="-mt-2"
                    width="20"
                    height="20"
                    viewBox="0 0 0.96 0.96"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <title>l</title>
                    <path
                      d="M0.757 0.779H0.364V0.144a0.009 0.009 0 0 0 -0.009 -0.009h-0.028a0.009 0.009 0 0 0 -0.009 0.009v0.672a0.009 0.009 0 0 0 0.009 0.009h0.43a0.009 0.009 0 0 0 0.009 -0.009v-0.028a0.009 0.009 0 0 0 -0.009 -0.009z"
                    />
                  </svg>
                  <el-popconfirm
                    title="Are you sure to delete this customized item?"
                    confirmButtonText="Delete"
                    cancelButtonText="Cancel"
                    @confirm="removeCustomerOverride(c.warehouses, w.id)"
                  >
                    <a
                      class="fas fa-trash-alt"
                      slot="reference"
                      v-on:click.stop
                    />
                  </el-popconfirm>
                  <label class="grow text-xs">{{ customerName(w.id) }}</label>
                  <div class="w-20">
                    <el-input v-model="w.rate" size="mini" type="number" />
                  </div>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import DoeFsc from "@/default/pages/base_rates/doe_fsc.vue";
import Regions from "@/default/pages/base_rates/regions.vue";
import Accessorial from "@/default/pages/base_rates/accessorial.vue";
export default {
  name: "CityFixedStatic",
  components: { DoeFsc, Regions, Accessorial },
  props: {
    hub: {
      type: Object,
      required: true,
    },
    baseRate: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      actions: ["setup", "dray", "drop", "hook", "return"],
      row: {},
      customer: {
        id: null,
        visible: false,
        items: [],
        current: null,
      },
      customers: {
        keyword: "",
        current: null,
        page: 1,
        total: 0,
        pageSize: 10,
        items: [],
        identifier: 1,
      },
      drivers: {
        keyword: "",
        current: null,
        page: 1,
        total: 0,
        pageSize: 10,
        items: [],
        identifier: 1,
      },
      cityFuseOptions: {
        shouldSort: true,
        threshold: 0.25,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: ["city"],
      },
      fsc: {
        customerId: null,
      },
      accessoriesReady: false,
    };
  },
  mounted() {
    this.refineData();
    this.customersPageChange(this.customers.page);
    this.driversPageChange(this.drivers.page);
  },
  watch: {
    customerCityKeyword() {
      this.customersPageChange(this.customers.page);
    },
    driverCityKeyword() {
      this.driversPageChange(this.drivers.page);
    },
  },
  computed: {
    customerCityKeyword() {
      return this.customers.keyword;
    },
    driverCityKeyword() {
      return this.drivers.keyword;
    },
    filteredCustomers() {
      let items = this.baseRate.customers;
      const term = this.customers.keyword.trim();
      if (term.length >= 2) {
        items = this.$search(term, items, this.cityFuseOptions).map(
          (o) => o.item,
        );
      }
      return items;
    },
    filteredDrivers() {
      let items = this.baseRate.drivers;
      const term = this.drivers.keyword.trim();
      if (term.length >= 2) {
        items = this.$search(term, items, this.cityFuseOptions).map(
          (o) => o.item,
        );
      }
      return items;
    },
    terminalNames() {
      return function (row) {
        const { terminals } = this.options;
        return (row.terminals || [])
          .map((id) => terminals.find((o) => o.id == id))
          .filter((o) => o)
          .map((o) => o.name);
      };
    },
    fscCustomerDisabled() {
      return function (customer) {
        return this.baseRate.fsc.some((o) => o.id == customer.id);
      };
    },
    regions() {
      return this.hub.regions;
    },
    driverMileageRate() {
      return false;
    },
    customerName() {
      return function (id) {
        return [
          ...(this.options.customers || []),
          ...(this.options.warehouses || []),
        ].find((o) => o.id == id)?.name;
      };
    },
    customerOptions() {
      return function (group) {
        const customers = (this.options.customers || []).filter(
          (o) => !(group || []).map((o) => o.id).includes(o.id),
        );
        const warehouses = (this.options.warehouses || []).filter(
          (o) => !(group || []).map((o) => o.id).includes(o.id),
        );
        if (this.customer.current) {
          return [{ label: "Warehouse", items: warehouses }];
        }
        return [
          { label: "Customer", items: customers },
          { label: "Warehouse", items: warehouses },
        ];
      };
    },
    searchCustomerPlaceholder() {
      return this.customer.current
        ? `Search Warehouse for ${this.customerName(this.customer.current.id)}`
        : "Search Customer/Warehouse";
    },
  },
  methods: {
    setTerminalSelectorVisible(row, visible) {
      this.$set(row, "terminalSelector", visible);
    },
    refineData() {
      const br = this.baseRate;
      this.$set(br, "drivers", br.drivers || []);
      this.$set(br, "customers", br.customers || []);
      this.$set(br, "driver_globals", br.driver_globals || {});

      ["hazmat", "tank"].forEach((key) => {
        const globalFees = br.driver_globals[key] || {};
        Object.keys(this.options.driverTypes).forEach((type) => {
          if (!Object.prototype.hasOwnProperty.call(globalFees, type)) {
            globalFees[type] = null;
          }
        });
        this.$set(br.driver_globals, key, globalFees);
      });

      br.drivers.forEach((row) => {
        this.initDriverRow(row);
      });

      br.customers.forEach((c) => {
        if (!c.regions) this.$set(c, "regions", {});
        this.regions.forEach((region) => {
          if (!Object.keys(c.regions).includes(region.name)) {
            this.$set(c.regions, region.name, {
              rate: 0,
              toll: 0,
              customers: [],
            });
          }
        });
      });
      this.$set(br, "accessories", br.accessories || []);
      br.accessories.forEach((c) => {
        if (!c.regions) this.$set(c, "regions", {});
        this.regions.forEach((region) => {
          if (!Object.keys(c.regions).includes(region.name)) {
            this.$set(c.regions, region.name, {
              customer: { fee: 0, fsc: 0 },
              driver: { fee: 0, fsc: 0, target: "customer" },
            });
          }
        });
      });
      this.accessoriesReady = true;
      this.addDoeFsc();
    },
    addDoeFsc() {
      if (!this.baseRate.fsc) {
        this.$set(this.baseRate, "fsc", []);
        this.baseRate.fsc.push({ mode: "doe", target: "driver", records: [] });
        this.baseRate.fsc.push({
          mode: "doe",
          target: "customer",
          records: [],
        });
      }
    },
    defaultDriverActions(previous = {}) {
      return {
        setup: previous.setup ?? null,
        dray: previous.dray ?? null,
        drop: previous.drop ?? null,
        hook: previous.hook ?? null,
        return: previous.return ?? null,
      };
    },
    initDriverRow(row) {
      if (row.city == null) this.$set(row, "city", "");
      if (row.manual == null) this.$set(row, "manual", false);
      if (!row.terminals) this.$set(row, "terminals", []);
      Object.keys(this.options.driverTypes || {}).forEach((type) => {
        if (!row[type]) {
          this.$set(row, type, this.defaultDriverActions());
        } else {
          this.actions.forEach((action) => {
            if (!Object.prototype.hasOwnProperty.call(row[type], action)) {
              this.$set(row[type], action, null);
            }
          });
        }
      });
    },
    addDriverCity() {
      this.drivers.keyword = "";
      const previous =
        this.baseRate.drivers[this.baseRate.drivers.length - 1] || {};
      const driver = {
        city: "",
        manual: false,
        terminals: [],
      };
      Object.keys(this.options.driverTypes || {}).forEach((type) => {
        driver[type] = this.defaultDriverActions({
          setup: previous[type]?.setup,
          return: previous[type]?.return,
        });
      });
      this.baseRate.drivers.push(driver);
      const groups = this._.chunk(this.baseRate.drivers, this.drivers.pageSize);
      this.driversPageChange(groups.length);
    },
    removeDriverCity(index) {
      const row = this.drivers.items[index];
      const gindex = this.baseRate.drivers.indexOf(row);
      if (gindex >= 0) this.baseRate.drivers.splice(gindex, 1);
      this.driversPageChange(this.drivers.page);
    },
    driversPageChange(val) {
      this.drivers.identifier++;
      this.drivers.total = this.filteredDrivers.length;
      const groups = this._.chunk(this.filteredDrivers, this.drivers.pageSize);
      const page = Math.min(val, groups.length);
      this.drivers.items = page == 0 ? [] : groups[page - 1];
      this.drivers.page = Math.max(page, 1);
    },
    focusDriverCity(item) {
      this.drivers.current = item;
    },
    manualDriverCity(item) {
      this.$set(item, "manual", !item.manual);
    },
    selectDriverCity(item) {
      const { current } = this.drivers;
      if (current) {
        current.dup = false;
        if (item) {
          const rows = this.baseRate.drivers.filter((o) => o != current);
          const dup = rows.filter((o) => o.city == current.city).length > 0;
          this.$set(current, "dup", dup);
        }
      }
    },
    checkDriverCity() {
      const { current } = this.drivers;
      if (current) {
        let [city, state] = current.city.split(",").map((o) => o.trim());
        city = city
          .split(/\s/)
          .map((o) => o.titleize())
          .join(" ");
        state = state.toUpperCase();
        const name = [city, state].join(", ");
        const rows = this.baseRate.drivers.filter((o) => o != current);
        const dup = rows.filter((o) => o.city == name).length > 0;
        this.$set(current, "city", name);
        this.$set(current, "dup", dup);
      }
    },
    addCustomerCity() {
      this.customers.keyword = "";
      const attrs = { rate: 0, toll: 0 };
      const map = this.regions.map((region) => [
        region.name,
        { ...attrs, customers: [] },
      ]);
      const regions = Object.fromEntries(new Map(map));
      this.baseRate.customers.push({ city: "", regions });
      const groups = this._.chunk(
        this.baseRate.customers,
        this.customers.pageSize,
      );
      this.customersPageChange(groups.length);
    },
    removeCustomerCity(index) {
      const { page } = this.customers;
      const gindex = (page - 1) * this.customers.pageSize + index;
      this.baseRate.customers.splice(gindex, 1);
      this.customersPageChange(page);
    },
    focusCustomerCity(item) {
      this.customers.current = item;
    },
    manualCustomerCity(item) {
      this.$set(item, "manual", !item.manual);
    },
    selectCustomerCity(item) {
      const { current } = this.customers;
      if (current) {
        current.dup = false;
        if (item) {
          const rows = this.baseRate.customers.filter((o) => o != current);
          const dup = rows.filter((o) => o.city == current.city).length > 0;
          this.$set(current, "dup", dup);
        }
      }
    },
    checkCustomerCity() {
      const { current } = this.customers;
      if (current) {
        let [city, state] = current.city.split(",").map((o) => o.trim());
        city = city
          .split(/\s/)
          .map((o) => o.titleize())
          .join(" ");
        state = state.toUpperCase();
        const name = [city, state].join(", ");
        const rows = this.baseRate.customers.filter((o) => o != current);
        const dup = rows.filter((o) => o.city == name).length > 0;
        this.$set(current, "city", name);
        this.$set(current, "dup", dup);
      }
    },
    customersPageChange(val) {
      this.customers.identifier++;
      this.customers.total = this.filteredCustomers.length;
      const groups = this._.chunk(
        this.filteredCustomers,
        this.customers.pageSize,
      );
      const page = Math.min(val, groups.length);
      this.customers.items = page == 0 ? [] : groups[page - 1];
      this.customers.page = Math.max(page, 1);
    },
    specificCustomers(target, property) {
      target[property] ||= [];
      const selector = '.el-popper[aria-hidden="false"] .customers';
      const div = document.querySelector(selector);
      if (div) {
        this.customer.visible = true;
        div.appendChild(this.$refs.customerOverrides);
        this.customer.items = target[property];
      }
    },
    addCustomerOverride(group) {
      if (this.customer.current) {
        this.customer.current.warehouses.push({
          id: this.customer.id,
          rate: 0,
        });
      } else {
        group.push({ id: this.customer.id, rate: 0 });
      }
      this.customer.id = null;
    },
    removeCustomerOverride(group, id) {
      let index = group.findIndex((o) => o.id == id);
      if (index >= 0) group.splice(index, 1);
    },
    specificCustomerWarehouses(item) {
      this.$set(item, "warehouses", item.warehouses || []);
      if (this.customer.current?.id == item.id) {
        this.$set(this.customer, "current", null);
      } else {
        this.$set(this.customer, "current", item);
      }
    },
    addCustomerFsc() {
      this.baseRate.fsc.push({
        id: this.fsc.customerId,
        mode: "doe",
        target: "customer",
        records: [],
      });
      this.fsc.customerId = null;
    },
    removeFsc(index) {
      this.baseRate.fsc.splice(index, 1);
    },
    addAccessory(accessory) {
      this.baseRate.accessories.push(accessory);
    },
    removeAccessory(index) {
      this.baseRate.accessories.splice(index, 1);
    },
    exportCsv(target) {
      this.getDownload(`/api/v1/base_rates/export.csv`, {
        hub_id: this.hub.id,
        target,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.city-fixed-static::v-deep {
  @apply flex flex-col gap-1;
  .el-tabs__content {
    @apply h-[calc(100vh-275px)] overflow-auto #{!important};
  }
  .terminal-names {
    @apply flex-1 min-w-0 h-6 flex flex-nowrap items-center gap-0.5 overflow-x-auto overflow-y-hidden text-left text-2xs font-bold #{!important};
    .item {
      @apply shrink-0 bg-sky-200 px-1 rounded whitespace-nowrap;
    }
  }
  .global-fees {
    @apply flex items-center gap-2;
    input.el-input__inner {
      @apply h-6 #{!important};
    }
  }
}
</style>
